import { useState, useEffect } from "react";
import {
  adminGetSocialEvents,
  adminCreateSocialEvent,
  adminUpdateSocialEvent,
  adminDeleteSocialEvent,
  adminSearchHostBusinesses,
  adminGetProviderClasses,
  adminUploadImage,
} from "../../../../api/apiEndpoints";
import {
  SectionTitle, Card, Row, Input, Select, Button, Table, Th, Td, Badge, FormGrid, Label, FormField, StatusText, ModalOverlay, Modal, ModalHeader, ModalTitle, CloseButton, EmptyState,
} from "../styles";

const EVENTS_PER_PAGE = 15;
const STATE_OPTIONS = ["FL", "NY"];

const BLANK_EVENT = {
  title: "",
  photoUrl: "",
  eventDate: "",
  eventTime: "",
  city: "",
  state: "FL",
  locationName: "",
  price: "",
  rsvpLink: "",
  isFreeForMembers: false,
  isPublished: false,
  // Class check-in linking (optional): pick a host studio + a class from its schedule.
  hostBusinessId: "",
  hostBusinessName: "",
  provider: "",
  externalLocationId: "",
  externalClassId: "",
  className: "",
  classStartTime: "",
  classMeta: null as Record<string, any> | null,
};

type EventForm = typeof BLANK_EVENT;

// Optional class-check-in linking: pick a host business (must have a booking integration),
// pick a class from its live schedule, set the check-in window, and attach a waiver.
const EventCheckinFields = ({ f, set }: { f: EventForm; set: (v: EventForm) => void }) => {
  const [hostQuery, setHostQuery] = useState(f.hostBusinessName || "");
  const [hostResults, setHostResults] = useState<any[]>([]);
  const [hostOpen, setHostOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [classDate, setClassDate] = useState(f.eventDate || "");
  const [classes, setClasses] = useState<any[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classErr, setClassErr] = useState("");

  useEffect(() => {
    if (!hostOpen) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminSearchHostBusinesses(hostQuery);
        setHostResults(res.data?.businesses ?? []);
      } catch { setHostResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [hostQuery, hostOpen]);

  const selectHost = (b: any) => {
    set({ ...f, hostBusinessId: b.businessId, hostBusinessName: b.name || "", provider: b.provider || "",
      externalLocationId: b.externalLocationId || "", externalClassId: "", className: "", classStartTime: "", classMeta: null });
    setHostQuery(b.name || ""); setHostOpen(false); setClasses([]);
  };
  const clearHost = () => {
    set({ ...f, hostBusinessId: "", hostBusinessName: "", provider: "", externalLocationId: "",
      externalClassId: "", className: "", classStartTime: "", classMeta: null });
    setHostQuery(""); setClasses([]);
  };

  const loadClasses = async () => {
    if (!f.hostBusinessId || !f.provider) return;
    if (f.provider === "marianatek" && !f.externalLocationId) {
      setClassErr("This studio's MarianaTek integration has no location ID. Add it under Gyms → Manage, then try again.");
      setClasses([]);
      return;
    }
    setLoadingClasses(true); setClassErr("");
    try {
      const start = classDate ? new Date(classDate + "T00:00:00").toISOString() : undefined;
      const end = classDate ? new Date(classDate + "T23:59:59").toISOString() : undefined;
      const res = await adminGetProviderClasses(f.provider, f.hostBusinessId, start, end, f.externalLocationId);
      const items = res.data?.items ?? [];
      setClasses(items);
      if (!items.length) setClassErr("No classes found for that day.");
    } catch (e: any) {
      setClassErr(e?.response?.data?.error || "Could not load classes."); setClasses([]);
    } finally { setLoadingClasses(false); }
  };

  const classId = (c: any) => String(c.id ?? c.classId ?? c.classSessionId ?? c.sessionId ?? c.scheduleId ?? c.eventId ?? c.activityId ?? "");
  const classStart = (c: any) => c.attributes?.start_datetime ?? c.startDateTime ?? c.start ?? c.startTime ?? c.classStartTime ?? c.activityDate ?? null;
  const classTitle = (c: any) => c.attributes?.class_type_display ?? c.attributes?.name ?? c.name ?? c.className ?? c.title ?? c.activityName ?? "Class";
  const classInstructor = (c: any) => c.attributes?.instructor_names?.[0] ?? c.instructor ?? "";
  const classLabel = (c: any) => {
    const t = classStart(c);
    const when = t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    return [when, classTitle(c), classInstructor(c)].filter(Boolean).join(" · ");
  };
  const selectClass = (c: any) => {
    const t = classStart(c);
    set({ ...f, externalClassId: classId(c), className: classTitle(c),
      classStartTime: t ? new Date(t).toISOString() : "", classMeta: c });
  };

  return (
    <div style={{ marginTop: 20, padding: 16, border: "1px solid #eee", borderRadius: 10, background: "#fafafa" }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Class check-in (optional)</div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
        Link this event to a class at a partner studio so members can sign a waiver and book in. Leave blank for a one-off event.
      </div>

      {/* Host business search */}
      <FormField style={{ position: "relative" }}>
        <Label>Host studio</Label>
        {f.hostBusinessId ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Badge color="#e0e7ff">{f.hostBusinessName || f.hostBusinessId}{f.provider ? ` · ${f.provider}` : ""}</Badge>
            <Button variant="secondary" onClick={clearHost} style={{ padding: "4px 10px", fontSize: 12 }}>Change</Button>
          </div>
        ) : (
          <>
            <Input
              placeholder="Search studios with booking integrations…"
              value={hostQuery}
              onFocus={() => setHostOpen(true)}
              onChange={(e) => { setHostQuery(e.target.value); setHostOpen(true); }}
            />
            {hostOpen && (hostResults.length > 0 || searching) && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff",
                border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,.08)" }}>
                {searching && <div style={{ padding: 10, fontSize: 12, color: "#888" }}>Searching…</div>}
                {hostResults.map((b: any) => (
                  <div key={b.businessId} onClick={() => selectHost(b)}
                    style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between" }}>
                    <span>{b.name || b.businessId}</span>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>{b.provider}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </FormField>

      {/* Class picker */}
      {f.hostBusinessId && (
        <FormField style={{ marginTop: 12 }}>
          <Label>Class</Label>
          <Row style={{ gap: 8, alignItems: "center" }}>
            <Input type="date" value={classDate} onChange={(e) => setClassDate(e.target.value)} style={{ maxWidth: 180 }} />
            <Button variant="secondary" onClick={loadClasses} disabled={loadingClasses} style={{ padding: "6px 12px", fontSize: 13 }}>
              {loadingClasses ? "Loading…" : "Load classes"}
            </Button>
          </Row>
          {classErr && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{classErr}</div>}
          {classes.length > 0 && (
            <Select style={{ marginTop: 8 }} value={f.externalClassId}
              onChange={(e) => { const c = classes.find((x) => classId(x) === e.target.value); if (c) selectClass(c); }}>
              <option value="">Select a class…</option>
              {classes.map((c: any) => <option key={classId(c)} value={classId(c)}>{classLabel(c)}</option>)}
            </Select>
          )}
          {f.externalClassId && (
            <div style={{ fontSize: 12, color: "#16a34a", marginTop: 6 }}>
              Linked: {f.className}{f.classStartTime ? ` · ${new Date(f.classStartTime).toLocaleString()}` : ""}
            </div>
          )}
        </FormField>
      )}
    </div>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<EventForm>(BLANK_EVENT);
  const [createLoading, setCreateLoading] = useState(false);
  const [createStatus, setCreateStatus] = useState<{ msg: string; ok: boolean } | null>(null);

  const [editEvent, setEditEvent] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<EventForm>(BLANK_EVENT);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState<{ msg: string; ok: boolean } | null>(null);

  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(0);

  const handlePhotoUpload = async (file: File, f: EventForm, set: (v: EventForm) => void) => {
    setUploadingPhoto(true);
    try {
      const { data } = await adminUploadImage(file);
      set({ ...f, photoUrl: data.url });
    } catch {
      alert("Image upload failed. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await adminGetSocialEvents();
      setEvents(res.data?.events ?? []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const buildPayload = (f: EventForm) => ({
    title: f.title.trim(),
    eventDate: f.eventDate,
    eventTime: f.eventTime.trim(),
    city: f.city.trim(),
    state: f.state,
    locationName: f.locationName.trim(),
    price: f.price.trim(),
    isFreeForMembers: f.isFreeForMembers,
    isPublished: f.isPublished,
    photoUrl: f.photoUrl.trim() || null,
    rsvpLink: f.rsvpLink.trim() || null,
    // Class check-in linking — only send when a host is chosen; null clears it.
    hostBusinessId: f.hostBusinessId || null,
    externalClassId: f.externalClassId || null,
    externalLocationId: f.externalLocationId || null,
    classMeta: f.classMeta || null,
    className: f.className || null,
    classStartTime: f.classStartTime || null,
  });

  const canCreate = form.title && form.eventDate && form.eventTime && form.city &&
    form.state && form.locationName && form.price;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreateLoading(true);
    setCreateStatus(null);
    try {
      await adminCreateSocialEvent(buildPayload(form));
      setCreateStatus({ msg: `Event "${form.title}" created.`, ok: true });
      setForm(BLANK_EVENT);
      setCreateFormKey((k) => k + 1);
      fetchEvents();
    } catch (e: any) {
      setCreateStatus({ msg: e?.response?.data?.message || "Failed to create event.", ok: false });
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (ev: any) => {
    setEditEvent(ev);
    setEditForm({
      title: ev.title ?? "",
      photoUrl: ev.photo_url ?? "",
      eventDate: (ev.event_date ?? "").slice(0, 10),
      eventTime: ev.event_time ?? "",
      city: ev.city ?? "",
      state: ev.state ?? "FL",
      locationName: ev.location_name ?? "",
      price: ev.price ?? "",
      rsvpLink: ev.rsvp_link ?? "",
      isFreeForMembers: !!ev.is_free_for_members,
      isPublished: !!ev.is_published,
      hostBusinessId: ev.host_business_id ?? "",
      hostBusinessName: ev.host_business_name ?? "",
      provider: ev.provider ?? "",
      externalLocationId: ev.external_location_id ?? "",
      externalClassId: ev.external_class_id ?? "",
      className: ev.class_name ?? "",
      classStartTime: ev.class_start_time ?? "",
      classMeta: ev.external_class_meta ?? null,
    });
    setEditStatus(null);
  };

  const handleEdit = async () => {
    if (!editEvent) return;
    setEditLoading(true);
    setEditStatus(null);
    try {
      await adminUpdateSocialEvent(editEvent.id, buildPayload(editForm));
      setEditStatus({ msg: "Saved.", ok: true });
      fetchEvents();
    } catch (e: any) {
      setEditStatus({ msg: e?.response?.data?.message || "Failed to save.", ok: false });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (ev: any) => {
    if (!window.confirm(`Delete event "${ev.title}"? This cannot be undone.`)) return;
    setDeleteLoading(ev.id);
    try {
      await adminDeleteSocialEvent(ev.id);
      fetchEvents();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to delete.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const visible = events.filter((ev: any) => !search ||
    (ev.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (ev.location_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (ev.city ?? "").toLowerCase().includes(search.toLowerCase()));

  const totalPages = Math.max(1, Math.ceil(visible.length / EVENTS_PER_PAGE));
  const paged = visible.slice((page - 1) * EVENTS_PER_PAGE, page * EVENTS_PER_PAGE);

  const renderFields = (f: EventForm, set: (v: EventForm) => void, checkinKey: string) => (
    <>
      <FormGrid>
        <FormField>
          <Label>Title *</Label>
          <Input placeholder="e.g. Sunrise Yoga & Coffee" value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} />
        </FormField>
        <FormField>
          <Label>Date *</Label>
          <Input type="date" value={f.eventDate} onChange={(e) => set({ ...f, eventDate: e.target.value })} />
        </FormField>
        <FormField>
          <Label>Time *</Label>
          <Input placeholder="e.g. 7:00 AM" value={f.eventTime} onChange={(e) => set({ ...f, eventTime: e.target.value })} />
        </FormField>
        <FormField>
          <Label>State *</Label>
          <Select value={f.state} onChange={(e) => set({ ...f, state: e.target.value })}>
            {STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormField>
        <FormField>
          <Label>City *</Label>
          <Input placeholder="e.g. Miami" value={f.city} onChange={(e) => set({ ...f, city: e.target.value })} />
        </FormField>
        <FormField>
          <Label>Location name *</Label>
          <Input placeholder="e.g. South Beach" value={f.locationName} onChange={(e) => set({ ...f, locationName: e.target.value })} />
        </FormField>
        <FormField>
          <Label>Price *</Label>
          <Input placeholder="e.g. Free or $20" value={f.price} onChange={(e) => set({ ...f, price: e.target.value })} />
        </FormField>
        <FormField>
          <Label>Photo (optional)</Label>
          <input
            type="file"
            accept="image/*"
            disabled={uploadingPhoto}
            onChange={(e) => { const file = e.target.files?.[0]; if (file) handlePhotoUpload(file, f, set); e.target.value = ""; }}
          />
          {uploadingPhoto && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Uploading…</div>}
          {f.photoUrl && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
              <img src={f.photoUrl} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }} />
              <Button variant="secondary" onClick={() => set({ ...f, photoUrl: "" })} style={{ padding: "4px 10px", fontSize: 12 }}>Remove</Button>
            </div>
          )}
        </FormField>
        <FormField>
          <Label>RSVP link (optional)</Label>
          <Input placeholder="https://…" value={f.rsvpLink} onChange={(e) => set({ ...f, rsvpLink: e.target.value })} />
        </FormField>
      </FormGrid>
      <Row style={{ marginTop: 12, gap: 24, alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={f.isFreeForMembers} onChange={(e) => set({ ...f, isFreeForMembers: e.target.checked })} />
          Free for members
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={f.isPublished} onChange={(e) => set({ ...f, isPublished: e.target.checked })} />
          Published (visible in app)
        </label>
      </Row>
      <EventCheckinFields key={checkinKey} f={f} set={set} />
    </>
  );

  return (
    <>
      <Card>
        <SectionTitle>Create Event</SectionTitle>
        {renderFields(form, setForm, `create-${createFormKey}`)}
        <Row style={{ marginTop: 12 }}>
          <Button onClick={handleCreate} disabled={!canCreate || createLoading}>
            {createLoading ? "Creating…" : "Create Event"}
          </Button>
        </Row>
        {createStatus && <StatusText success={createStatus.ok}>{createStatus.msg}</StatusText>}
      </Card>

      <Card>
        <SectionTitle>Existing Events</SectionTitle>
        <Row style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search title, location, city…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ maxWidth: 480 }}
          />
        </Row>
        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState>No events found.</EmptyState>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr>
                    <Th>Title</Th>
                    <Th>Date</Th>
                    <Th>Time</Th>
                    <Th>City</Th>
                    <Th>State</Th>
                    <Th>Location</Th>
                    <Th>Price</Th>
                    <Th>Free</Th>
                    <Th>Published</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((ev: any) => (
                    <tr key={ev.id}>
                      <Td><strong>{ev.title}</strong></Td>
                      <Td data-label="Date">{(ev.event_date ?? "").slice(0, 10) || "—"}</Td>
                      <Td data-label="Time">{ev.event_time || "—"}</Td>
                      <Td data-label="City">{ev.city || "—"}</Td>
                      <Td data-label="State">{ev.state || "—"}</Td>
                      <Td data-label="Location">{ev.location_name || "—"}</Td>
                      <Td data-label="Price">{ev.price || "—"}</Td>
                      <Td data-label="Free"><Badge color={ev.is_free_for_members ? "#dcfce7" : "#f1f5f9"}>{ev.is_free_for_members ? "Yes" : "No"}</Badge></Td>
                      <Td data-label="Published"><Badge color={ev.is_published ? "#dcfce7" : "#fee2e2"}>{ev.is_published ? "Live" : "Draft"}</Badge></Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Button variant="secondary" onClick={() => openEdit(ev)} style={{ padding: "4px 10px", fontSize: 12 }}>Edit</Button>
                          <Button variant="danger" onClick={() => handleDelete(ev)} disabled={deleteLoading === ev.id} style={{ padding: "4px 10px", fontSize: 12 }}>
                            {deleteLoading === ev.id ? "…" : "Delete"}
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Row style={{ marginTop: 16, justifyContent: "center" }}>
                <Button variant="secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
                <span style={{ fontSize: 13, color: "#555", alignSelf: "center" }}>Page {page} of {totalPages}</span>
                <Button variant="secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</Button>
              </Row>
            )}
          </>
        )}
      </Card>

      {editEvent && (
        <ModalOverlay onClick={() => setEditEvent(null)}>
          <Modal onClick={(e: any) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <ModalHeader>
              <ModalTitle>Edit — {editEvent.title}</ModalTitle>
              <CloseButton onClick={() => setEditEvent(null)}>×</CloseButton>
            </ModalHeader>
            {renderFields(editForm, setEditForm, `edit-${editEvent.id}`)}
            <Row style={{ marginTop: 16 }}>
              <Button onClick={handleEdit} disabled={editLoading}>{editLoading ? "Saving…" : "Save Changes"}</Button>
              <Button variant="secondary" onClick={() => setEditEvent(null)}>Cancel</Button>
            </Row>
            {editStatus && <StatusText success={editStatus.ok}>{editStatus.msg}</StatusText>}
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default EventsPage;
